import React from 'react';
import { Box, IconButton, Tooltip, Avatar } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { useAuthStore } from '../../stores/authStore';
import { useCartStore } from '../../stores/cartStore';
import { palette } from '../../theme/colors';

const SIDEBAR_WIDTH = 80;

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  path: string;
  isActive: boolean;
  onClick: () => void;
  badge?: number;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, isActive, onClick, badge }) => (
  <Tooltip title={label} placement="right" arrow>
    <Box
      sx={{
        position: 'relative',
        mb: 1,
      }}
    >
      <IconButton
        onClick={onClick}
        sx={{
          width: 48,
          height: 48,
          borderRadius: '14px',
          color: isActive ? palette.textOnPrimary : palette.sidebarText,
          backgroundColor: isActive ? palette.primary : 'transparent',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: isActive ? palette.primary : palette.sidebarHover,
            color: isActive ? palette.textOnPrimary : palette.surface,
          },
        }}
      >
        {icon}
      </IconButton>
      {badge !== undefined && badge > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 18,
            height: 18,
            borderRadius: '50%',
            backgroundColor: palette.accent,
            color: palette.textOnAccent,
            fontSize: '0.65rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {badge > 9 ? '9+' : badge}
        </Box>
      )}
    </Box>
  </Tooltip>
);

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuthStore();
  const itemCount = useCartStore((state) => state.getItemCount());

  const mainNavItems = [
    { icon: <HomeOutlinedIcon />, label: 'Home', path: '/' },
    { icon: <StorefrontOutlinedIcon />, label: 'Stores', path: '/stores' },
    { icon: <InventoryOutlinedIcon />, label: 'Products', path: '/products' },
    { icon: <ShoppingCartOutlinedIcon />, label: 'Cart', path: '/cart', badge: itemCount },
    { icon: <ReceiptLongOutlinedIcon />, label: 'Track Order', path: '/track-reservation' },
  ];

  const bottomNavItems = [
    { icon: <SettingsOutlinedIcon />, label: 'Settings', path: '/account-settings' },
    { icon: <HelpOutlineOutlinedIcon />, label: 'Help', path: '#' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isPathActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        minWidth: SIDEBAR_WIDTH,
        height: '100vh',
        backgroundColor: palette.sidebarBg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 3,
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1200,
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          backgroundColor: palette.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 4,
          cursor: 'pointer',
          transition: 'transform 0.2s ease',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        }}
        onClick={() => navigate('/')}
      >
        <StorefrontOutlinedIcon sx={{ color: palette.textOnPrimary, fontSize: 24 }} />
      </Box>

      {/* Main Navigation */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flex: 1,
        }}
      >
        {mainNavItems.map((item) => (
          <NavItem
            key={item.path}
            icon={item.icon}
            label={item.label}
            path={item.path}
            isActive={isPathActive(item.path)}
            onClick={() => navigate(item.path)}
            badge={item.badge}
          />
        ))}
      </Box>

      {/* Bottom Navigation */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mt: 'auto',
        }}
      >
        {bottomNavItems.map((item) => (
          <NavItem
            key={item.path}
            icon={item.icon}
            label={item.label}
            path={item.path}
            isActive={isPathActive(item.path)}
            onClick={() => item.path !== '#' && navigate(item.path)}
          />
        ))}

        {isAuthenticated ? (
          <Tooltip title="Logout" placement="right" arrow>
            <IconButton
              onClick={handleLogout}
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                color: palette.sidebarText,
                mt: 1,
                '&:hover': {
                  backgroundColor: `${palette.error}20`,
                  color: palette.error,
                },
              }}
            >
              <LogoutOutlinedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Login" placement="right" arrow>
            <IconButton
              onClick={() => navigate('/login')}
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                color: palette.sidebarText,
                mt: 1,
                '&:hover': {
                  backgroundColor: palette.sidebarHover,
                  color: palette.surface,
                },
              }}
            >
              <PersonOutlineOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* User Avatar */}
        {isAuthenticated && (
          <Tooltip title="My Account" placement="right" arrow>
            <Avatar
              onClick={() => navigate('/account-settings')}
              sx={{
                width: 40,
                height: 40,
                mt: 2,
                cursor: 'pointer',
                backgroundColor: palette.sidebarHover,
                color: palette.surface,
                fontSize: '0.9rem',
                fontWeight: 600,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: palette.primary,
                  color: palette.textOnPrimary,
                },
              }}
            >
              U
            </Avatar>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export const SIDEBAR_WIDTH_VALUE = SIDEBAR_WIDTH;
